import React from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'

/**
 * Shared chrome for the two Wilson wizards (appraisal + offer to purchase).
 *
 * Figma reference: the `Form` frame is identical on every step of both flows —
 * 4097:104771 / 4097:106203 (appraisal desktop), 4096:95826 (appraisal mobile),
 * 4095:85112 ff. (offer to purchase). Its stack is, top to bottom:
 *
 *   progress-steps-bar          h24        (rendered by <StepIndicator />)
 *   gap/gap-8                   32
 *   Frame 753 "address row"     h32        <AddressSummary />
 *   gap/gap-8                   32
 *   "<set>" — prompt h24 + gap/gap-6 24 + controls
 *   gap/gap-8                   32
 *   Frame 713 — buttons h36, gap/gap-2 8
 *
 * NOTE the order: the progress bar comes FIRST and the address line sits below it.
 */

/**
 * Figma renders the property line as `10 Fairweather Avenue, *Suburbville*` —
 * Headings/headlineSm (Merriweather 400, font/size/lg 24 / lineHeight 32 / -0.1px)
 * with the suburb portion in italic. Real addresses arrive from
 * `/api/address-lookup` as a single string, so the italic run is derived by
 * splitting on the first comma rather than from an authored `*asterisk*` marker
 * (which is what `src/utilities/renderHeading.tsx` handles for CMS strings).
 */
export function renderAddress(address: string): React.ReactNode {
  const comma = address.indexOf(',')
  if (comma === -1) return address
  return (
    <>
      {address.slice(0, comma + 1)}
      <em className="italic">{address.slice(comma + 1)}</em>
    </>
  )
}

/**
 * Address row.
 *
 * Desktop — Figma `Frame 753` (4097:104808) is one 32px-tall row: the address text
 * followed by a `link button` instance pinned to the RIGHT edge of the 704px form
 * column (x=651, w=53 → 704).
 * Tablet / mobile — Figma `Frame 754` (4097:106487) is 48 tall: the address wraps and
 * the link drops BELOW it, left-aligned, at `gap/gap-2` 8px (link y=32 under a 24-tall
 * text node). Confirmed on the 768 render 4097:103350 as well.
 *
 * Link: `link button` — Body/bodySmAlt 14/16, underlined, `theme/foreground`.
 */
const ADDRESS_ROW =
  'flex w-full flex-col items-start gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-3'

export function AddressSummary({ address, changeUrl }: { address: string; changeUrl: string }) {
  return (
    <div data-visual="wizard-address-summary" className={ADDRESS_ROW}>
      <p className="font-display text-display-lg font-normal text-foreground">
        {renderAddress(address)}
      </p>
      <Button asChild variant="link" size="mini" className="shrink-0">
        <Link href={changeUrl}>Change?</Link>
      </Button>
    </div>
  )
}

/** Same row, but the "Change?" affordance is a button rather than a route link. */
export function AddressSummaryButton({
  address,
  onChange,
}: {
  address: string
  onChange: () => void
}) {
  return (
    <div data-visual="wizard-address-summary" className={ADDRESS_ROW}>
      <p className="font-display text-display-lg font-normal text-foreground">
        {renderAddress(address)}
      </p>
      <Button type="button" variant="link" size="mini" className="shrink-0" onClick={onChange}>
        Change?
      </Button>
    </div>
  )
}

/**
 * Step prompt — Figma `Body/bodyMdAlt`: Lato **400** (not semibold), font/size/base 16,
 * line-height bound to font/size/lg (24 desktop / 22 tablet / 20 mobile),
 * `theme/foreground`. Followed by `gap/gap-6` (24px) before the controls.
 */
export function StepPrompt({ children }: { children: React.ReactNode }) {
  return <p className="mb-6 text-body-md font-normal text-foreground">{children}</p>
}

/** Inline validation message — Body/bodySm on `theme/destructive`. */
export function StepError({ children }: { children: React.ReactNode }) {
  return <p className="mt-2 text-body-sm font-light text-destructive">{children}</p>
}

/** Figma `Frame 713` — primary + ghost buttons, `gap/gap-2` 8px, 32px above. */
export function StepActions({ children }: { children: React.ReactNode }) {
  return <div className="mt-8 flex items-center gap-2">{children}</div>
}

/** Loading placeholder shown while the wizard verifies its sessionStorage guards. */
export function StepLoading() {
  return (
    <div className="py-16">
      <div className="size-6 animate-spin rounded-full border-2 border-muted border-t-accent" />
    </div>
  )
}
